import { SearchPageBase } from '@common/SearchPageBase';
import { createTableLinkWithOpenNewTabColumn } from '@common/tableColumn.services';
import {
  Column,
  ComponentType,
  createTableColumn,
  createTableCell,
  Grid,
  PaginatedTable,
  Accordion,
  Tooltip,
} from '@gcl/react-core';
import { routes } from '@routes/routes.models';
import * as React from 'react';
import { useNavigate } from '@routes/routes.hooks';
import { useHasPermission, useProjectId } from '@config/config.hooks';
import { IProjectUserProfileOption } from '@userProfile/projectUserProfile.models';
import { useBreakPoints } from '@common/breakpoints.hooks';
import { ETwcTwcmApplicationPermission } from '@userProfile/permission/permission.constants';
import { useFetchAllProjectTwcGeRequests } from '../projectTwcGeRequest.queries';
import { EProjectTwcGeRequestApplicationType, EProjectTwcGeRequestStatus } from '../projectTwcGeRequest.constants';
import { IProjectTwcGeRequest } from '../projectTwcGeRequest.models';
import { useFetchAllTwcGeRequestProposedAsOptions } from '../projectTwcGeRequestProposedAs/projectTwcGeRequestProposedAs.queries';
import './ProjectTwcGeRequestListPage.scss';
import { IButtonNavBarItem } from '@app/ButtonNavBar';
import { ApiErrorDisplay } from '@core/ApiErrorDisplay';

export interface IProjectTwcGeRequestListPageProps {
  applicationType: EProjectTwcGeRequestApplicationType;
}

export const ProjectTwcGeRequestListPage: React.FunctionComponent<IProjectTwcGeRequestListPageProps> = ({
  applicationType,
}) => {
  const projectId = useProjectId();
  const { navigate, generateRoutePath, projectNo } = useNavigate();
  const applicationTypeName = React.useMemo(() => {
    return `${applicationType === EProjectTwcGeRequestApplicationType.TWC ? 'TWC' : 'GE'} Request`;
  }, [applicationType]);

  const hasInitRight = useHasPermission(ETwcTwcmApplicationPermission.INITATE_TWC);
  const { isXs, isSm, isMd, isLg, isXl } = useBreakPoints();
  const {
    data: proposedAsOptions,
    isLoading: isLoadingAllTwcGeRequestProposedAsOptions,
    error: errorAllTwcGeRequestProposedAsOptions,
  } = useFetchAllTwcGeRequestProposedAsOptions({
    isDirty: false,
    shouldConvertOptions: true,
  })({ projectId });

  const {
    data: projectTwcGeRequests,
    isLoading: isLoadingProjectTwcGeRequests,
    isRefetching: isRefetchingProjectTwcGeRequests,
    error: errorProjectTwcGeRequests,
  } = useFetchAllProjectTwcGeRequests({
    projectId,
    applicationType,
  });

  const requestMap = React.useMemo(() => {
    const map: { [key: string]: IProjectTwcGeRequest[] } = {};
    if (
      !proposedAsOptions ||
      !projectTwcGeRequests ||
      proposedAsOptions.length <= 0 ||
      projectTwcGeRequests.length <= 0
    )
      return map;

    if (applicationType === EProjectTwcGeRequestApplicationType.GE) return { GE: projectTwcGeRequests };

    proposedAsOptions.forEach((proposedAs) => {
      map[`${proposedAs?.data?.name} ${proposedAs?.data?.fullName}`] = projectTwcGeRequests.filter(
        (request) => request.proposedAs === proposedAs.data?.id,
      );
    });
    return map;
  }, [JSON.stringify(proposedAsOptions), JSON.stringify(projectTwcGeRequests)]);

  const checkIfUserLocked = (projectUserProfile?: IProjectUserProfileOption) => {
    return !projectUserProfile || projectUserProfile?.isLocked;
  };

  // #region Table Columns
  const columns: Column[] = React.useMemo(() => {
    return [
      {
        ...createTableLinkWithOpenNewTabColumn<IProjectTwcGeRequest>(
          'projectUserProfile.displayName',
          'Nominated Staff',
          ({ item, cellExtras }) => {
            return generateRoutePath(
              applicationType === EProjectTwcGeRequestApplicationType.TWC
                ? routes.users.twcRequest.detail.path
                : routes.users.geRequest.detail.path,
              { id: item.id.toString() },
            );
          },
          ({ item }, link) => {
            return (
              <div
                className={`gcl-gems-twc-ge-request-list-table-nominated-staff-cell gcl-fl-md ${
                  checkIfUserLocked(item.projectUserProfile) ? 'locked' : ''
                }`}
              >
                {link}
              </div>
            );
          },
          true,
          true,
        ),
        width: 150,
        minWidth: 150,
        maxWidth: 150,
      },
      {
        ...createTableColumn({
          Header: 'Approved Scope of Works',
          accessor: 'responsibility',
          disableSortBy: true,
          disableResizing: true,
          Cell: createTableCell<string, any>(
            (value: string, item: IProjectTwcGeRequest, index: number, cellExtras: { [key: string]: any }) => {
              return (
                <div>
                  <Tooltip
                    overlay={
                      <div className="gcl-gems-twc-ge-request-list-table-scope-of-work-cell-tooltip">
                        {item.responsibility}
                      </div>
                    }
                    id={`gcl-gems-twc-ge-request-list-table-scope-of-work-cell-tooltip-${item.id}}`}
                  >
                    <div
                      className={`${
                        checkIfUserLocked(item.projectUserProfile) ? 'locked' : ''
                      } gcl-txt-of-l1 gcl-gems-twc-ge-request-list-table-scope-of-work-cell`}
                    >
                      {value}
                    </div>
                  </Tooltip>
                </div>
              );
            },
          ),
        } as Column),
        width: undefined,
        maxWidth: undefined,
        minWidth: undefined,
      },
      {
        ...createTableColumn({
          Header: 'Status',
          accessor: 'status',
          disableSortBy: true,
          disableResizing: true,
          Cell: createTableCell<EProjectTwcGeRequestStatus, any>(
            (
              value: EProjectTwcGeRequestStatus,
              item: IProjectTwcGeRequest,
              index: number,
              cellExtras: { [key: string]: any },
            ) => {
              let statusStr = '';
              let className = '';
              switch (value) {
                case EProjectTwcGeRequestStatus.DRAFT:
                  statusStr = 'Draft';
                  className = 'draft';
                  break;
                case EProjectTwcGeRequestStatus.WAITING_FOR_PM:
                  statusStr = 'Waiting for PM';
                  className = 'waiting-for-pm';
                  break;
                case EProjectTwcGeRequestStatus.REJECTED_BY_PM:
                  statusStr = 'PM Rejected';
                  className = 'rejected-by-pm';
                  break;
                case EProjectTwcGeRequestStatus.WAITING_FOR_EM:
                  statusStr = 'Waiting for EM';
                  className = 'waiting-for-em';
                  break;
                case EProjectTwcGeRequestStatus.REJECTED_BY_EM:
                  statusStr = 'EM Rejected';
                  className = 'rejected-by-em';
                  break;
                case EProjectTwcGeRequestStatus.WAITING_FOR_HOE:
                  statusStr = 'Waiting for HOE';
                  className = 'waiting-for-hoe';
                  break;
                case EProjectTwcGeRequestStatus.REJECTED_BY_HOE:
                  statusStr = 'HOE Rejected';
                  className = 'rejected-by-hoe';
                  break;
                case EProjectTwcGeRequestStatus.CANCELLED:
                  statusStr = 'Cancelled';
                  className = 'cancelled';
                  break;
                case EProjectTwcGeRequestStatus.COMPLETED:
                  statusStr = 'Approved';
                  className = 'completed';
                  break;
                case EProjectTwcGeRequestStatus.REVOKE:
                  statusStr = 'Revoked';
                  className = 'revoked';
                  break;
                default:
                  break;
              }

              if (checkIfUserLocked(item.projectUserProfile)) {
                statusStr = 'Locked';
                className = `${className} locked`;
              }

              return <div className={`${className} gcl-gems-twc-ge-request-list-table-status-cell`}>{statusStr}</div>;
            },
          ),
        } as Column),
        width: 120,
        minWidth: 120,
        maxWidth: 120,
      },
    ];
  }, []);
  // #endregion Table Columns

  return (
    <>
      <ApiErrorDisplay
        errors={[
          {
            error: errorProjectTwcGeRequests,
            genericError: `Failed to get ${
              applicationType === EProjectTwcGeRequestApplicationType.TWC ? 'TWC' : 'GE'
            } Request`,
          },
          {
            error: errorAllTwcGeRequestProposedAsOptions,
            genericError: `Failed to get ${
              applicationType === EProjectTwcGeRequestApplicationType.TWC ? 'TWC' : 'GE'
            } Request options`,
          },
        ]}
      />
      <SearchPageBase
        data={projectTwcGeRequests}
        breadcrumbItems={[
          {
            text: `${projectNo} - Home`,
            prefixIconName: 'House',
            onClick: () => {
              navigate(routes.t1.path);
            },
          },
          {
            prefixIconName: 'People',
            text: 'Users',
            onClick: () => {
              navigate(routes.users.path);
            },
          },
          {
            prefixIconName: 'People',
            text: `${applicationTypeName} List`,
            active: true,
          },
        ]}
        title={`${applicationTypeName} List`}
        isTableRefetching={isRefetchingProjectTwcGeRequests}
        isTableLoading={isLoadingProjectTwcGeRequests || isLoadingAllTwcGeRequestProposedAsOptions}
        buttonNavBarItem={[
          ...(hasInitRight
            ? ([
                {
                  text: 'New',
                  onClick: () =>
                    navigate(
                      applicationType === EProjectTwcGeRequestApplicationType.TWC
                        ? routes.users.twcRequest.create.path
                        : routes.users.geRequest.create.path,
                    ),
                  iconName: 'Plus',
                  componentType: ComponentType.Primary,
                  showNoneInArchivedProject: true,
                },
              ] as IButtonNavBarItem[])
            : []),
          // {
          // text: 'Print',
          // iconName: 'ArrowBarDown',
          // componentType: ComponentType.Secondary,
          // component: (
          //   <DownloadButton
          //     exportFunction={exportTransmittalSearchExcelAsync}
          //     defaultExportProps={{
          //       projectId,
          //       sortByQuery,
          //       filter: transmittalFilterObj,
          //     }}
          //   />
          // ),
          // },
        ]}
        noContentLabel={`No ${applicationTypeName} created yet`}
        onRenderTable={() => {
          return (
            <Grid
              container
              alignItems="top"
              justifyContent="space-between"
              spacing={1}
              className="gcl-gems-project-twc-ge-request-list-table-container"
            >
              {requestMap &&
                Object.keys(requestMap).map((proposedAs) => (
                  <Grid
                    item
                    xs={12}
                    xl={Object.keys(requestMap).length > 1 ? 6 : 12}
                    key={proposedAs}
                    className="gcl-gems-project-twc-ge-request-list-table"
                  >
                    <Accordion
                      allowClickHeaderToExpand
                      content={{
                        id: proposedAs,
                        onRenderBody: () => {
                          return (
                            <PaginatedTable
                              columns={columns}
                              items={requestMap[proposedAs] || []}
                              hoverHighlight
                              disablePagination
                              allowExpandRow={false}
                              allowSelectRow={false}
                              responsive={false}
                              cellExtras={{ navigate }}
                              classNames={{
                                dataCell: (item, column, cellExtras) => {
                                  if (column.Header === 'Approved Scope of Works')
                                    return 'gcl-gems-twc-ge-request-list-table-scope-of-work-cell-container';
                                  return '';
                                },
                              }}
                            />
                          );
                        },
                        onRenderHeader: proposedAs,
                        defaultIsExpanded: true,
                      }}
                    />
                  </Grid>
                ))}
            </Grid>
          );
        }}
      />
    </>
  );
};
